import { useCallback, useEffect, useState } from 'react'
import { Button, Card, Chip, Input, Label, Table, TextField, ToggleButton, ToggleButtonGroup } from '@heroui/react'
import { Check, Messages } from '../admin/parts'
import { Notice } from '../admin/settingsKit'
import { DAYS } from '../layout/media'
import { api, type BranchSeat, type Campaign, type PlaylistSummary } from '../lib/api'
import {
  PLACE_KEYS,
  PLACES,
  STATE_KEYS,
  bodyOf,
  branchNote,
  campaignBranches,
  campaignDayKeys,
  campaignHours,
  campaignProblems,
  campaignsStamp,
  formOf,
  type CampaignForm,
} from '../lib/signageEdit'
import { explain } from '../lib/staffErrors'
import { formatMoment, type StaffKey, type StaffLanguage } from '../lib/staffText'
import { Icon } from '../ui/icons'
import { MultiSelect } from '../ui/MultiSelect'
import { useStaffText } from '../ui/StaffText'
import { ClockField, DayField, Empty, PickOne, SignageModal } from './signageKit'
import { NowPanel } from './signageNow'

/** A YYYY-MM-DD as "9 October", the calendar day itself whatever this browser's zone. */
function calendarDay(day: string, lang: StaffLanguage): string {
  return day ? formatMoment(`${day}T12:00:00Z`, lang, { date: true, zone: 'UTC' }) : '—'
}

/** How a state reads as a chip: running is live, the ones that stop a campaign showing warn. */
const STATE_TONE: Partial<Record<NonNullable<Campaign['state']>, 'success' | 'warning'>> = {
  running: 'success',
  noSeat: 'warning',
  empty: 'warning',
  outranked: 'warning',
}

/** Branches shown in a row before "+n". */
const ROW_BRANCHES = 2

/** Campaigns: the list with their states, one being edited in a dialog, and what plays now. */
export function CampaignsTab() {
  const { say, lang } = useStaffText()
  const [campaigns, setCampaigns] = useState<Campaign[]>([])
  const [branches, setBranches] = useState<BranchSeat[]>([])
  const [playlists, setPlaylists] = useState<PlaylistSummary[]>([])
  const [today, setToday] = useState('')
  const [loaded, setLoaded] = useState(false)
  const [editing, setEditing] = useState<{ id?: string; form: CampaignForm } | null>(null)
  const [notice, setNotice] = useState('')
  const [done, setDone] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    const [c, p] = await Promise.all([api.campaigns(), api.playlists()])
    setCampaigns(c.campaigns)
    setBranches(c.branches)
    setToday(c.today)
    setPlaylists(p.playlists)
  }, [])
  useEffect(() => {
    load()
      .catch((err) => setNotice(explain(err, lang, 'common.unreachable')))
      .finally(() => setLoaded(true))
  }, [load, lang])

  const run = async (fn: () => Promise<unknown>, doneKey: StaffKey = 'common.saved') => {
    setBusy(true)
    setNotice('')
    setDone('')
    try {
      await fn()
      setEditing(null)
      await load()
      setDone(say(doneKey))
    } catch (err) {
      setNotice(explain(err, lang, 'common.unreachable'))
    } finally {
      setBusy(false)
    }
  }

  const save = () => {
    if (!editing) return
    const stored = editing.id ? campaigns.find((c) => c.id === editing.id) : undefined
    const problems = campaignProblems(editing.form, today, stored?.endDate)
    if (problems.length > 0) {
      setNotice(problems.map((k) => say(k)).join(' '))
      return
    }
    const body = bodyOf(editing.form)
    void run(() => (editing.id ? api.saveCampaign(editing.id, body) : api.createCampaign(body)))
  }

  const edit = (next: { id?: string; form: CampaignForm } | null) => {
    setNotice('')
    setEditing(next)
  }
  const playlistName = (id: string) => playlists.find((p) => p.id === id)?.name ?? ''
  const newButton = (
    <Button variant="primary" size="sm" onPress={() => edit({ form: formOf(undefined, today) })}>
      <Icon name="plus" size={16} /> {say('signage.newCampaign')}
    </Button>
  )
  const editingName = editing?.id ? campaigns.find((c) => c.id === editing.id)?.name : undefined

  return (
    <>
      <Card className="signage-panel">
        <Messages error={editing ? '' : notice} notice={done} />
        {campaigns.length > 0 && (
          <div className="signage-toolbar">
            <span className="signage-count">{say('signage.nCampaigns', { n: campaigns.length })}</span>
            {newButton}
          </div>
        )}
        {!loaded ? (
          <p className="signage-loading">{say('common.loading')}</p>
        ) : campaigns.length === 0 ? (
          <Empty icon="clock" title={say('signage.campaigns.empty')} action={newButton} />
        ) : (
          <Table variant="secondary" className="signage-table">
            <Table.ScrollContainer>
              <Table.Content
                aria-label={say('signage.tab.campaigns')}
                onRowAction={(key) => {
                  const c = campaigns.find((x) => x.id === key)
                  if (c) edit({ id: c.id, form: formOf(c, today) })
                }}
              >
                <Table.Header>
                  <Table.Column isRowHeader>{say('signage.name')}</Table.Column>
                  <Table.Column>{say('signage.state')}</Table.Column>
                  <Table.Column>{say('signage.dates')}</Table.Column>
                  <Table.Column>{say('signage.when')}</Table.Column>
                  <Table.Column>{say('signage.branches')}</Table.Column>
                  {PLACES.length > 1 ? <Table.Column>{say('signage.places')}</Table.Column> : null}
                  <Table.Column>{say('signage.priority')}</Table.Column>
                </Table.Header>
                <Table.Body>
                  {campaigns.map((c) => {
                    const chosen = branches.filter((b) => c.branchIds.includes(b.id))
                    const warnedBy = chosen.find((b) => branchNote(b))
                    const all = campaignBranches(c, branches, (b) => (branchNote(b) ? say(branchNote(b)!) : ''))
                    const state = c.state ?? 'scheduled'
                    const tone = STATE_TONE[state]
                    return (
                      <Table.Row key={c.id} id={c.id}>
                        <Table.Cell>
                          <span className="signage-table__name">{c.name}</span>
                          <span className="signage-table__sub">{playlistName(c.playlistId)}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <Chip size="sm" variant={tone ? 'soft' : 'secondary'} color={tone ?? 'default'}>
                            {say(STATE_KEYS[state])}
                          </Chip>
                        </Table.Cell>
                        <Table.Cell className="signage-table__nowrap">
                          {calendarDay(c.startDate, lang)} – {calendarDay(c.endDate, lang)}
                        </Table.Cell>
                        <Table.Cell>
                          {campaignDayKeys(c).map((k) => say(k)).join(', ') || say('signage.everyDay')}
                          <span className="signage-table__sub">{campaignHours(c) || say('signage.allDay')}</span>
                        </Table.Cell>
                        <Table.Cell>
                          <span className="signage-table__branches" title={all || undefined}>
                            {chosen.slice(0, ROW_BRANCHES).map((b) => b.name).join(', ') || '—'}
                            {chosen.length > ROW_BRANCHES && (
                              <Chip size="sm" variant="soft">
                                {say('signage.andMore', { n: chosen.length - ROW_BRANCHES })}
                              </Chip>
                            )}
                          </span>
                          {warnedBy && (
                            <span className="signage-table__warn">
                              <Icon name="alert" size={13} /> {say(branchNote(warnedBy)!)}
                            </span>
                          )}
                        </Table.Cell>
                        {PLACES.length > 1 ? <Table.Cell>{c.places.filter((p) => p in PLACE_KEYS).map((p) => say(PLACE_KEYS[p])).join(', ')}</Table.Cell> : null}
                        <Table.Cell>
                          {c.priority === 'high' ? (
                            <Chip size="sm" variant="primary" color="accent">
                              {say('signage.priority.high')}
                            </Chip>
                          ) : (
                            <span className="signage-table__sub">{say('signage.priority.normal')}</span>
                          )}
                        </Table.Cell>
                      </Table.Row>
                    )
                  })}
                </Table.Body>
              </Table.Content>
            </Table.ScrollContainer>
          </Table>
        )}
      </Card>
      <NowPanel branches={branches} stamp={campaignsStamp(campaigns)} />
      {editing && (
        <CampaignEditor
          title={editingName ?? say('signage.newCampaign')}
          form={editing.form}
          onChange={(form) => setEditing({ ...editing, form })}
          branches={branches}
          playlists={playlists}
          busy={busy}
          error={notice}
          onSave={save}
          onCancel={() => edit(null)}
          onDelete={editing.id ? () => void run(() => api.deleteCampaign(editing.id!), 'signage.deleted') : undefined}
        />
      )}
    </>
  )
}

function CampaignEditor({
  title,
  form,
  onChange,
  branches,
  playlists,
  busy,
  error,
  onSave,
  onCancel,
  onDelete,
}: {
  title: string
  form: CampaignForm
  onChange: (form: CampaignForm) => void
  branches: BranchSeat[]
  playlists: PlaylistSummary[]
  busy: boolean
  error: string
  onSave: () => void
  onCancel: () => void
  onDelete?: () => void
}) {
  const { say } = useStaffText()
  const [asking, setAsking] = useState(false)
  const set = (patch: Partial<CampaignForm>) => onChange({ ...form, ...patch })
  return (
    <SignageModal
      title={title}
      onClose={onCancel}
      footNote={
        onDelete && (
          <Button
            variant={asking ? 'danger' : 'danger-soft'}
            isDisabled={busy}
            onPress={() => {
              setAsking(!asking)
              if (asking) onDelete()
            }}
          >
            {say(asking ? 'signage.deleteAsk' : 'common.delete')}
          </Button>
        )
      }
      foot={
        <>
          <Button variant="tertiary" onPress={onCancel}>
            {say('common.cancel')}
          </Button>
          <Button variant="primary" isPending={busy} onPress={onSave}>
            {busy ? say('common.saving') : say('common.save')}
          </Button>
        </>
      }
    >
      <div className="signage-form">
        {error && <Notice>{error}</Notice>}
        <TextField className="signage-field" value={form.name} maxLength={80} onChange={(name) => set({ name })}>
          <Label>{say('signage.name')}</Label>
          <Input variant="secondary" />
        </TextField>
        <PickOne label={say('signage.playlist')} value={form.playlistId} onChange={(playlistId) => set({ playlistId })} options={playlists.map((p) => ({ value: p.id, label: p.name }))} />
        <div className="signage-form__pair">
          <DayField label={say('signage.from')} value={form.startDate} onChange={(startDate) => set({ startDate })} />
          <DayField label={say('signage.to')} value={form.endDate} min={form.startDate} onChange={(endDate) => set({ endDate })} />
        </div>
        <div className="signage-field">
          <span className="signage-field__label">{say('signage.days')}</span>
          <ToggleButtonGroup
            className="signage-toggles"
            size="sm"
            selectionMode="multiple"
            aria-label={say('signage.days')}
            selectedKeys={form.weekdays}
            onSelectionChange={(keys) => set({ weekdays: DAYS.filter((d) => keys.has(d)) })}
          >
            {DAYS.map((d) => (
              <ToggleButton key={d} id={d}>
                {say(`designs.media.day.${d}` as StaffKey)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        <Check label={say('signage.allDay')} checked={form.allDay} onChange={(allDay) => set({ allDay })} />
        {!form.allDay && (
          <div className="signage-form__pair">
            <ClockField label={say('designs.media.from')} value={form.from} onChange={(from) => set({ from })} />
            <ClockField label={say('designs.media.to')} value={form.to} onChange={(to) => set({ to })} />
          </div>
        )}
        <div className="signage-field">
          <span className="signage-field__label">{say('signage.priority')}</span>
          <ToggleButtonGroup
            className="signage-toggles"
            size="sm"
            selectionMode="single"
            disallowEmptySelection
            aria-label={say('signage.priority')}
            selectedKeys={[form.priority]}
            onSelectionChange={(keys) => {
              const next = [...keys][0]
              if (next === 'normal' || next === 'high') set({ priority: next })
            }}
          >
            {(['normal', 'high'] as const).map((p) => (
              <ToggleButton key={p} id={p}>
                {say(p === 'high' ? 'signage.priority.high' : 'signage.priority.normal')}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        <div className="signage-field">
          <span className="signage-field__label">{say('signage.branches')}</span>
          <MultiSelect
            options={branches.map((b) => ({ value: b.id, label: b.name, note: branchNote(b) ? say(branchNote(b)!) : undefined }))}
            value={form.branchIds}
            onChange={(branchIds) => set({ branchIds })}
            placeholder={say('signage.addBranches')}
            searchPlaceholder={say('signage.findBranch')}
          />
        </div>
        <div className="signage-field">
          <span className="signage-field__label">{say('signage.places')}</span>
          <ToggleButtonGroup
            className="signage-toggles"
            size="sm"
            selectionMode="multiple"
            aria-label={say('signage.places')}
            selectedKeys={form.places}
            onSelectionChange={(keys) => set({ places: PLACES.filter((p) => keys.has(p)) })}
          >
            {PLACES.map((p) => (
              <ToggleButton key={p} id={p}>
                {say(PLACE_KEYS[p])}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </div>
        <Check label={say('signage.on')} checked={form.active} onChange={(active) => set({ active })} />
      </div>
    </SignageModal>
  )
}
